// src/pages/prompts/PromptEditor.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  // FormControlLabel, // Removed
  // Switch, // Removed
  CircularProgress,
  Card,
  CardContent,
  useTheme,
  alpha,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  // Visibility as VisibilityIcon, // Not used in current simplified version
  // VisibilityOff as VisibilityOffIcon, // Not used
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { promptService } from '../../api/promptService';
import ReactMarkdown from 'react-markdown';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { vs2015 } from 'react-syntax-highlighter/dist/cjs/styles/hljs';

const PromptEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isEditing = !!id;

  const [prompt, setPrompt] = useState({
    name: '',
    content: '',
    // is_active_for_agent: false // Removed
  });
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  // const [previewVisible, setPreviewVisible] = useState(false); // Preview is now always visible
  const [copied, setCopied] = useState(false);

  const loadPrompt = useCallback(async () => {
    try {
      setLoading(true);
      const data = await promptService.getPromptById(id);
      // Ensure we don't try to set is_active_for_agent if it's not part of the state
      const { is_active_for_agent, ...restOfData } = data; // eslint-disable-line no-unused-vars
      setPrompt(restOfData);
    } catch (error) {
      toast.error('Failed to load prompt');
      console.error(error);
      navigate('/prompts');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (isEditing) {
      loadPrompt();
    }
  }, [isEditing, loadPrompt]);


  const handleChange = (e) => {
    const { name, value } = e.target; // Removed checked and type === 'checkbox' logic
    setPrompt(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    // Create a payload without is_active_for_agent
    const payload = {
        name: prompt.name,
        content: prompt.content,
    };
    if (!payload.name.trim() || !payload.content.trim()) {
        toast.error("Prompt name and content are required.");
        setSaving(false);
        return;
    }


    try {
      if (isEditing) {
        await promptService.updatePrompt(id, payload);
        toast.success('Prompt updated successfully');
      } else {
        // For create, if an agent_id should be optionally linked, it would be part of PromptCreate model.
        // Current API docs say `agent_id? (UUID for initial link)` for PromptCreate.
        // If we want to support this, we'd need an Agent selector on the create form.
        // For now, assume createPrompt only takes name and content.
        await promptService.createPrompt(payload);
        toast.success('Prompt created successfully');
      }
      navigate('/prompts');
    } catch (error) {
      toast.error('Failed to save prompt');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  // togglePreview removed as preview is always visible

  const copyPromptContent = () => {
    navigator.clipboard.writeText(prompt.content);
    setCopied(true);
    toast.success('Prompt content copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const CodeBlock = ({node, inline, className, children, ...props}) => {
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : 'text';

    return !inline ? (
      <SyntaxHighlighter
        style={vs2015}
        language={language}
        PreTag="div"
        {...props}
      >
        {String(children).replace(/\n$/, '')}
      </SyntaxHighlighter>
    ) : (
      <code className={className} {...props}>
        {children}
      </code>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

return (
  <Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
      <Typography variant="h4" component="h1">
        {isEditing ? 'Edit Prompt' : 'Create New Prompt'}
      </Typography>
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/prompts')}
      >
        Back to Prompts
      </Button>
    </Box>

    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
      {/* Editor Section */}
      <Paper
        component="form"
        onSubmit={handleSubmit}
        sx={{
          p: 3,
          flexGrow: 1,
          flexBasis: { xs: '100%', lg: '50%' },
          height: 'fit-content'
        }}
      >
        <TextField
          name="name"
          label="Prompt Name"
          value={prompt.name}
          onChange={handleChange}
          fullWidth
          required
          margin="normal"
          inputProps={{
            maxLength: 100
          }}
          helperText={`${prompt.name.length}/100 characters`}
        />

        <TextField
          name="content"
          label="Prompt Content (Markdown)"
          value={prompt.content}
          onChange={handleChange}
          fullWidth
          required
          multiline
          rows={20}
          margin="normal"
          sx={{
            fontFamily: 'monospace',
            '& .MuiInputBase-root': {
              fontFamily: 'monospace',
              fontSize: '0.9rem',
              lineHeight: 1.6
            }
          }}
          helperText="Write your system prompt here using Markdown. Preview appears on the right."
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          {/* is_active_for_agent Switch Removed */}
          <Box /> {/* Placeholder to keep layout for copy button */}

          <Tooltip title="Copy prompt content">
            <IconButton
              onClick={copyPromptContent}
              color={copied ? "success" : "default"}
            >
              <CopyIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button
            variant="contained"
            type="submit"
            disabled={saving}
            startIcon={<SaveIcon />}
            sx={{ px: 4 }}
          >
            {saving ? 'Saving...' : (isEditing ? 'Update Prompt' : 'Create Prompt')}
          </Button>
        </Box>
      </Paper>

      {/* Preview Section - Always visible */}
      <Card
        sx={{
          flexGrow: 1,
          flexBasis: { xs: '100%', lg: '50%' },
          maxHeight: { lg: '86vh' },
          overflowY: 'auto'
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box sx={{
            p: 2,
            backgroundColor: theme.palette.mode === 'dark'
              ? theme.palette.grey[900]
              : alpha(theme.palette.primary.main, 0.05),
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }}>
            <Typography variant="h6" gutterBottom>Live Preview</Typography>
            <Typography variant="caption" color="text.secondary">
              This is how your prompt will appear to the AI system
            </Typography>
          </Box>

          <Box sx={{
            p: 3,
            backgroundColor: theme.palette.background.default
          }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.background.paper, 0.2)
                  : alpha(theme.palette.background.paper, 0.7),
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                fontFamily: 'system-ui, -apple-system, sans-serif',
                fontSize: '0.9rem',
                lineHeight: 1.6,
                maxWidth: '100%',
                overflowX: 'auto'
              }}
            >
              <ReactMarkdown
                components={{
                  code: CodeBlock
                }}
              >
                {prompt.content || 'No content to preview'}
              </ReactMarkdown>
            </Paper>
          </Box>
        </CardContent>
      </Card>
    </Box>
  </Box>
);

};

export default PromptEditor;